import { UserEquipment, DefaultEquipment, ServiceRecord, RecordServiceRequest } from '../types/Equipment';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export const equipmentService = {
  async getUserEquipment(userId: string): Promise<UserEquipment[]> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch equipment');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  },

  async getEquipmentById(userId: string, equipmentId: string): Promise<UserEquipment> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}/${equipmentId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch equipment');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  },

  async createEquipment(equipment: Partial<UserEquipment>): Promise<UserEquipment> {
    try {
      const response = await fetch(`${API_URL}/equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(equipment),
      });
      if (!response.ok) {
        throw new Error('Failed to create equipment');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating equipment:', error);
      throw error;
    }
  },

  async updateEquipment(userId: string, equipmentId: string, equipment: UserEquipment): Promise<UserEquipment> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}/${equipmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(equipment),
      });
      if (!response.ok) {
        throw new Error('Failed to update equipment');
      }
      // API returns 204 No Content, so return the equipment passed in with updated timestamp
      return { ...equipment, updatedAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error updating equipment:', error);
      throw error;
    }
  },

  async deleteEquipment(userId: string, equipmentId: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}/${equipmentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete equipment');
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      throw error;
    }
  },

  async getDefaultEquipment(userId: string): Promise<DefaultEquipment> {
    try {
      const response = await fetch(`${API_URL}/equipment/default/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch default equipment');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching default equipment:', error);
      throw error;
    }
  },

  async saveDefaultEquipment(defaultEquipment: DefaultEquipment): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/equipment/default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defaultEquipment),
      });
      if (!response.ok) {
        throw new Error('Failed to save default equipment');
      }
    } catch (error) {
      console.error('Error saving default equipment:', error);
      throw error;
    }
  },

  async incrementDiveCount(userId: string, equipmentId: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/equipment/increment-dive-count/${userId}/${equipmentId}`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to increment dive count');
      }
    } catch (error) {
      console.error('Error incrementing dive count:', error);
      throw error;
    }
  },

  async getEquipmentTypes(): Promise<string[]> {
    try {
      const response = await fetch(`${API_URL}/equipment/types`);
      if (!response.ok) {
        throw new Error('Failed to fetch equipment types');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching equipment types:', error);
      throw error;
    }
  },

  async recordService(userId: string, equipmentId: string, request: RecordServiceRequest): Promise<UserEquipment> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}/${equipmentId}/service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      if (!response.ok) {
        throw new Error('Failed to record service');
      }
      return await response.json();
    } catch (error) {
      console.error('Error recording service:', error);
      throw error;
    }
  },

  async getServiceHistory(userId: string, equipmentId: string): Promise<ServiceRecord[]> {
    try {
      const response = await fetch(`${API_URL}/equipment/${userId}/${equipmentId}/service-history`);
      if (!response.ok) {
        throw new Error('Failed to fetch service history');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching service history:', error);
      throw error;
    }
  },

  async recalculateDiveCounts(userId: string): Promise<{ message: string; totalEquipment: number; totalDives: number }> {
    try {
      const response = await fetch(`${API_URL}/equipment/recalculate-dive-counts/${userId}`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to recalculate dive counts');
      }
      return await response.json();
    } catch (error) {
      console.error('Error recalculating dive counts:', error);
      throw error;
    }
  }
};
